import { ChainCodeErrorEnum } from "../../../enums/rpcErrorCodes";
import { isWrongNetworkError } from "../../../utils/wallet/activeSigner";

/**
 * What to tell someone whose signing-key registration just failed.
 *
 * This is a pure function on purpose: the message is the whole product surface
 * of a failed connect, and it deserves a test rather than living inline in a
 * hook that needs a provider, an account and a signer to exercise.
 *
 * The case that prompted it: registration asked a provider to sign for an
 * account that provider had never been granted, and the wallet answered with
 * EIP-1193 code 4100. The old copy was "Unable to register signing key. Please
 * try again.", which sends the user round a retry loop that cannot succeed.
 *
 * A NOTE ON WHAT NOT TO SAY HERE. My first attempt at this message told people
 * to uninstall their other wallet extensions. That was wrong twice over: it
 * blames the user for a defect the app owns (their other dapps work fine), and
 * it is not even the cause -- the app was signing through whichever connector
 * web3-react ranked first, not the wallet the user picked. The real fixes are in
 * useRegister (use the selected provider) and signing.ts (name the account).
 *
 * So this message states what the wallet said and the one thing the user can
 * actually check. It does not speculate about their browser.
 */
export const registerSigningKeyErrorMessage = (error: unknown): string => {
  const err = error as {
    code?: number;
    message?: string;
    name?: string;
  };

  // The user clicked reject. Their choice, and retrying is genuinely the fix.
  if (err?.code === ChainCodeErrorEnum.CANCELLED) {
    return "Signature request cancelled. Please try again.";
  }

  // Same situation, thrown by activeSigner instead. `getWeb3ProviderForChain`
  // now performs the chain check that useRegister used to hand-roll, and it
  // already names both chains -- so surface it verbatim rather than letting it
  // fall through to the generic "please try again", which is the retry loop this
  // whole function exists to avoid.
  if (isWrongNetworkError(error)) {
    return error.message;
  }

  // The two errors activeSigner/activeProvider raise before a provider even
  // exists. Both already carry a specific, actionable sentence -- "connect a
  // wallet" and "reconnect the one you chose" are different remedies, which is
  // why they are different errors -- so neither may fall through to the generic
  // retry advice. Step 1 routed registration through `getWeb3ProviderForChain`,
  // which made both newly reachable from this path.
  //
  // Matched by `name` rather than `instanceof`: it keeps the connectors module
  // graph out of this leaf file, and survives the module being mocked -- the same
  // reason `isWrongNetworkError` carries a name fallback of its own. (Not for ES5
  // prototype loss, which sibling comments in this repo cite: tsconfig targets
  // es2020 and browserslist resolves to nothing older than chrome 103, so
  // `instanceof` on an Error subclass works in this build.)
  if (
    err?.name === "SelectedWalletUnavailableError" ||
    err?.name === "NoWalletProviderError"
  ) {
    return err.message as string;
  }

  // The wallet will not act for this account at all -- not a rejected prompt.
  if (err?.code === ChainCodeErrorEnum.UNAUTHORIZED) {
    return (
      "Your wallet did not authorise this account for signing. Check that the " +
      "account you want to trade from is the one currently selected in your " +
      "wallet and connected to this site, then try connecting again."
    );
  }

  return "Unable to register signing key. Please try again.";
};
